import { noop } from 'lodash';

import CommentAPI from '@/collection/api/CommentAPI';
import { getDefaultCollectionSettings } from '@/collection/helpers/getDefaultCollectionSettings';
import { CollectionUpdateRequest } from '@/collection/types/CollectionUpdateRequest';
import useStore from '@/common/hooks/useStore';
import { DriveAPI } from '@/drive/api/DriveAPI';
import {
  BaseCollectionItem,
  CollectionItem,
  CollectionSubscriptionItem,
  DriveItem,
  DriveItemType,
  ProductItem,
  SpectrumItem,
  UrlItem,
} from '@/drive/entities/DriveItem/DriveItem';
import { DriveContainerId, DriveItemId } from '@/drive/entities/DriveItemId';
import { DriveItemsResponse } from '@/drive/entities/DriveItemsResponse';
import { TransientDriveItem } from '@/drive/entities/transientDriveItem';
import { toDriveItemsErrorResponse } from '@/drive/helpers/toDriveItemsErrorResponse';
import RecentDestinationStorage from '@/drive/services/RecentDestinationStorage';
import { DriveModifierEventEmitter } from '@/drive/stores/DriveModifierStore/DriveModifierEventEmitter';
import {
  AddSuccessItem,
  DriveAddOptions,
  DriveCopyOptions,
  DriveItemPatch,
  DriveModifierEventType,
  DriveWriteOptions,
} from '@/drive/stores/DriveModifierStore/DriveModifierStore.types';
import {
  getDuplicateTitle,
  toAddSuccessItem,
  toPrecedingNodeIds,
} from '@/drive/stores/DriveModifierStore/DriveModifierStore.utils';
import { TemporaryDriveItemFactory } from '@/drive/stores/DriveModifierStore/TemporaryDriveItemFactory';
import { AccessInheritanceResponse } from '@/drive/types/AccessInheritanceResponse';
import { DriveItemRequest } from '@/drive/types/DriveItemRequest';
import { PinnedUrl } from '@/forwarding/entities/PinnedItem/PinnedUrl';
import PinnedItemsStore, { pinnedItemsStore as globalPinnedItemsStore } from '@/forwarding/stores/PinnedItemsStore';
import { cleanUpForm, ForwardForm } from '@/forwarding/types/ForwardForm';
import { BaseProductType } from '@/product/constants/productTypes';
import { ProductId } from '@/product/entity/Product';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { ProjectDataAccess } from '@/security/types/DataAccess';

export class DriveModifierStore {
  private emitter = new DriveModifierEventEmitter();
  private temporaryItemFactory = new TemporaryDriveItemFactory();

  event$ = this.emitter.event$;

  constructor(
    private pinnedItemsStore: PinnedItemsStore,
    private driveAPI = new DriveAPI(),
    private commentAPI = new CommentAPI(),
  ) {}

  async deleteItem(item: DriveItem) {
    const pinnedItem = item.toPinnedItem();

    if (pinnedItem instanceof PinnedUrl) {
      this.pinnedItemsStore.unpin(pinnedItem.id);
    }

    this.emitter.deleteApply(item);

    await this.withErrorEvent(DriveModifierEventType.Delete, this.driveAPI.deleteById(item.id));

    this.emitter.deleteSuccess(item);
  }

  notifyAccessChange = (itemId: DriveItemId) => {
    this.emitter.accessChangeSuccess(itemId);
  };

  async reorder(parentId: DriveContainerId, orderedIds: DriveItemId[]) {
    this.emitter.reorderApply(parentId, orderedIds);

    try {
      await this.driveAPI.reorder(parentId, orderedIds);
    } catch {
      this.emitter.error(DriveModifierEventType.Reorder);
    }
  }

  async createFolder(form: ForwardForm, parentId: DriveContainerId, shouldInheritAccess = true) {
    const temporaryItem = this.temporaryItemFactory.createFolder(form);

    this.emitter.addApply([temporaryItem], parentId);

    const res = await this.withErrorEvent(
      DriveModifierEventType.Add,
      this.driveAPI.batchCreate([
        {
          type: DriveItemType.Folder,
          destinationFolderId: parentId,
          inheritAccess: shouldInheritAccess,
          ...cleanUpForm(form, { withPreview: true }),
        },
      ]),
    );

    return this.handleAddResponse(res, [temporaryItem], parentId);
  }

  async createUrl(form: ForwardForm, parentId: DriveContainerId, shouldInheritAccess = true) {
    const temporaryItem = this.temporaryItemFactory.createUrl(form);

    this.emitter.addApply([temporaryItem], parentId);

    const res = await this.withErrorEvent(
      DriveModifierEventType.Add,
      this.driveAPI.batchCreate([
        {
          type: DriveItemType.Url,
          destinationFolderId: parentId,
          inheritAccess: shouldInheritAccess,
          ...cleanUpForm(form, { isUrl: true, withPreview: true }),
        },
      ]),
    );

    return this.handleAddResponse(res, [temporaryItem], parentId);
  }

  async createCollection(
    form: ForwardForm,
    productType: BaseProductType,
    parentId: DriveContainerId,
    productIds: ProductId[] = [],
    shouldInheritAccess = true,
  ): Promise<DriveItemsResponse<CollectionItem>> {
    const temporaryItem = this.temporaryItemFactory.createCollection(form, productType);

    this.emitter.addApply([temporaryItem], parentId);

    const res = await this.withErrorEvent(
      DriveModifierEventType.Add,
      this.driveAPI.createCollection({
        destinationFolderId: parentId,
        title: form.title,
        description: form.description,
        type: temporaryItem.collectionType,
        productIds,
        access: GlobalAccessLevel.Restricted,
        dataAccess: ProjectDataAccess.HideProjectSource,
        settings: getDefaultCollectionSettings(productType),
        inheritAccess: shouldInheritAccess,
      }),
    );

    return this.handleAddResponse(res, [temporaryItem], parentId);
  }

  async updateItem(item: DriveItem, form: ForwardForm): Promise<DriveItemsResponse> {
    const req = cleanUpForm(form, { isUrl: item instanceof UrlItem, withPreview: item.canChangePreview() });

    item.setTitle(req.title ?? '');
    item.setDescription(req.description ?? '');

    if (item instanceof UrlItem && req.url !== undefined) {
      item.setUrl(req.url);
    }

    if (req.preview !== undefined) {
      item.setPreview(req.preview ? { url: req.preview } : null);
    }

    const patch: DriveItemPatch = { id: item.id, title: item.title, description: item.description ?? '' };

    this.emitter.updateApply(patch);

    const res = await this.withErrorEvent(
      DriveModifierEventType.Update,
      this.driveAPI.batchUpdate([{ id: item.id, inheritAccess: false, ...req }]),
    );

    if (res.hasErrors()) {
      this.emitter.error(DriveModifierEventType.Update);
    } else {
      this.emitter.updateSuccess(patch);
    }

    return res;
  }

  async updateCollection(item: CollectionItem, request: CollectionUpdateRequest): Promise<DriveItemsResponse> {
    const patch: DriveItemPatch = { id: item.id, title: request.title, description: request.description };

    this.emitter.updateApply(patch);

    const response = await this.withErrorEvent(
      DriveModifierEventType.Update,
      this.driveAPI.updateCollection(item.id, {
        title: request.title,
        description: request.description,
        settings: request.settings,
      }),
    );

    if (!response.hasErrors()) {
      this.emitter.updateSuccess(patch);
    } else {
      this.emitter.error(DriveModifierEventType.Update);
    }

    return response;
  }

  async addToFolder(
    requests: DriveItemRequest[],
    sourceItems: Nullable<TransientDriveItem>[],
    form: ForwardForm,
    parentId: DriveContainerId,
    { readOnly = false, shouldInheritAccess = false, withPreview = false }: DriveAddOptions = {},
  ) {
    const temporaryItems = requests.map((_, idx) => {
      const source = sourceItems[idx];

      return source ? this.temporaryItemFactory.createCopy(source, form, readOnly) : null;
    });

    this.emitter.addApply(
      temporaryItems.filter((item): item is DriveItem => item !== null),
      parentId,
      toPrecedingNodeIds(requests, temporaryItems),
    );

    const res = await this.withErrorEvent(
      DriveModifierEventType.Add,
      this.driveAPI.batchCreate(
        requests.map((req) => ({
          inheritAccess: shouldInheritAccess,
          ...req,
          ...cleanUpForm(form, { isUrl: req.type === DriveItemType.Url, readOnly, withPreview }),
          destinationFolderId: parentId,
        })),
      ),
    );

    return this.handleAddResponse(res, temporaryItems, parentId);
  }

  async duplicate(item: DriveItem, parentId: DriveContainerId): Promise<DriveItemsResponse> {
    const source = item instanceof SpectrumItem ? item.toProduct() : item;
    const form: ForwardForm = { ...source.toFormData(), title: getDuplicateTitle(source) };

    const withPreview = source.canChangePreview() && Boolean(source.preview?.url);

    if (source instanceof BaseCollectionItem) {
      return this.copyDriveItem(source, form, parentId, { precedingNodeId: source.id });
    }

    return this.addToFolder(
      [{ copyFrom: source.id, type: source.type, precedingNodeId: source.id }],
      [source],
      form,
      parentId,
      { withPreview },
    );
  }

  async addToCollection(
    parentId: DriveItemId,
    collectionId: string,
    productIds: ProductId[],
    form: Nullable<ForwardForm> = null,
    { readOnly = false, shouldInheritAccess = false }: DriveWriteOptions = {},
  ): Promise<AccessInheritanceResponse[]> {
    const accessChanges = await this.driveAPI.addToCollection(parentId, productIds, shouldInheritAccess, {
      suppressErrorNotifications: true,
    });

    await this.createCollectionComment(collectionId, productIds[0], form, readOnly);

    RecentDestinationStorage.add(parentId);
    this.emitter.addSuccess([], parentId, false);

    return accessChanges;
  }

  async removeFromCollection(collectionNodeId: DriveItemId, productIds: ProductId[]): Promise<void> {
    await this.driveAPI.removeFromCollection(collectionNodeId, productIds);
  }

  async sortCollectionProducts(collectionNodeId: DriveItemId, productIds: number[]): Promise<void> {
    await this.driveAPI.sortCollectionProducts(collectionNodeId, productIds);
  }

  async subscribeToCollection(
    collectionNodeId: DriveItemId,
    parentId: DriveContainerId,
  ): Promise<DriveItemsResponse<CollectionItem>> {
    let res: DriveItemsResponse<CollectionItem>;

    try {
      res = await this.driveAPI.subscribeToCollection(collectionNodeId, parentId);
    } catch (error) {
      this.emitter.error(DriveModifierEventType.Add);

      return toDriveItemsErrorResponse<CollectionItem>(error, 'Failed to subscribe to collection');
    }

    if (res.hasErrors()) {
      this.emitter.error(DriveModifierEventType.Add);
    } else {
      RecentDestinationStorage.add(parentId);
      this.emitter.addSuccess([], parentId, false);
    }

    return res;
  }

  async materializeCollectionSubscription(
    item: CollectionSubscriptionItem,
    parentId: DriveContainerId,
  ): Promise<DriveItemsResponse<CollectionItem>> {
    const temporaryItem = this.temporaryItemFactory.createMaterializedCollection(item);

    this.emitter.addApply([temporaryItem], parentId);

    try {
      const res = await this.driveAPI.materializeCollectionSubscription(item.id, parentId);

      return this.handleAddResponse(res, [temporaryItem], parentId);
    } catch (error) {
      this.emitter.error(DriveModifierEventType.Add);

      return toDriveItemsErrorResponse<CollectionItem>(error, 'Failed to copy collection');
    }
  }

  async copyDriveItem(
    item: DriveItem,
    form: ForwardForm,
    parentId: DriveContainerId,
    { readOnly = false, shouldInheritAccess = false, precedingNodeId }: DriveCopyOptions = {},
  ): Promise<DriveItemsResponse> {
    const temporaryItem = this.temporaryItemFactory.createCopy(item, form, readOnly);

    const precedingNodeIdByTempId = new Map<DriveItemId, DriveItemId>();

    if (precedingNodeId !== undefined) {
      precedingNodeIdByTempId.set(temporaryItem.id, precedingNodeId);
    }

    this.emitter.addApply([temporaryItem], parentId, precedingNodeIdByTempId);

    const res = await this.withErrorEvent(
      DriveModifierEventType.Add,
      this.driveAPI.copyDriveItem(
        item.id,
        parentId,
        cleanUpForm(form, { readOnly }),
        shouldInheritAccess,
        precedingNodeId,
      ),
    );

    return this.handleAddResponse(res, [temporaryItem], parentId);
  }

  async move(
    item: DriveItem,
    form: Nullable<ForwardForm>,
    parentId: DriveContainerId,
    currentParentId: DriveContainerId,
    { readOnly = false, shouldInheritAccess = false }: DriveWriteOptions = {},
  ) {
    const formData = form ? cleanUpForm(form, { isUrl: item.type === DriveItemType.Url, readOnly }) : {};

    this.emitter.moveApply(item, parentId, currentParentId);

    const res = await this.withErrorEvent(
      DriveModifierEventType.Move,
      this.driveAPI.batchUpdate([
        {
          ...formData,
          id: item.id,
          type: item.type,
          destinationFolderId: parentId,
          inheritAccess: shouldInheritAccess,
        },
      ]),
    );

    if (res.hasErrors()) {
      this.emitter.error(DriveModifierEventType.Move);
    } else {
      RecentDestinationStorage.add(parentId);

      this.emitter.moveSuccess(res.getItems()[0]!, parentId, currentParentId);
    }

    return res;
  }

  async moveToCollection(
    item: ProductItem,
    parentId: DriveItemId,
    collectionId: string,
    currentParentId: DriveContainerId,
    form: Nullable<ForwardForm> = null,
    { readOnly = false, shouldInheritAccess = false }: DriveWriteOptions = {},
  ): Promise<AccessInheritanceResponse[]> {
    this.emitter.moveApply(item, parentId, currentParentId);

    const accessChanges = await this.withErrorEvent(
      DriveModifierEventType.Move,
      this.driveAPI.moveToCollection(parentId, [item.id], shouldInheritAccess, { suppressErrorNotifications: true }),
    );

    await this.createCollectionComment(collectionId, item.productId, form, readOnly);

    RecentDestinationStorage.add(parentId);

    this.emitter.moveSuccess(item, parentId, currentParentId);

    return accessChanges;
  }

  // temporary function. Collection Comment code will be moved to backend in next release
  private async createCollectionComment(
    collectionId: string,
    productId: ProductId | undefined,
    form: Nullable<ForwardForm>,
    readOnly: boolean,
  ): Promise<void> {
    const description = form ? cleanUpForm(form, { readOnly }).description : null;

    if (description && collectionId.length > 0 && productId) {
      await this.commentAPI.createCollectionComment(collectionId, productId, description).catch(noop);
    }
  }

  private async withErrorEvent<T>(type: DriveModifierEventType, request: Promise<T>): Promise<T> {
    try {
      return await request;
    } catch (error) {
      this.emitter.error(type);

      throw error;
    }
  }

  private handleAddResponse<T extends DriveItem>(
    response: DriveItemsResponse<T>,
    temporaryItems: Nullable<DriveItem>[],
    parentId: DriveContainerId,
  ): DriveItemsResponse<T> {
    const allResponses = response.getAll();
    const items = temporaryItems
      .map((temp, idx) => (temp ? toAddSuccessItem(temp.id, allResponses[idx]) : null))
      .filter((item): item is AddSuccessItem => item !== null);

    RecentDestinationStorage.add(parentId);
    this.emitter.addSuccess(items, parentId, items.length === temporaryItems.length);

    return response;
  }
}

export const useDriveModifierStore = () => useStore(DriveModifierStore);

export const driveModifierStore = new DriveModifierStore(globalPinnedItemsStore);
